import {
    type Request,
    type RequestHistoryEntry,
    RequestStatus,
} from '@features/requests/types';

/**
 * State Transition Utilities
 * Manages request status transitions and history
 *
 * Status values follow the API spec (`'DRAFT' | 'SUBMITTED' | 'APPROVED' |
 * 'REJECTED' | 'MODIFIED'`) plus the local `'CANCELLED'` state.
 */

/**
 * Defines which status transitions are allowed
 */
const ALLOWED_TRANSITIONS: Map<RequestStatus, RequestStatus[]> = new Map([
    // DRAFT can transition to SUBMITTED
    [RequestStatus.DRAFT, [RequestStatus.SUBMITTED]],
    // SUBMITTED can transition to APPROVED, REJECTED, or CANCELLED
    [
        RequestStatus.SUBMITTED,
        [
            RequestStatus.APPROVED,
            RequestStatus.REJECTED,
            RequestStatus.CANCELLED,
        ],
    ],
    // REJECTED can be resubmitted (transitions back to SUBMITTED)
    [RequestStatus.REJECTED, [RequestStatus.SUBMITTED]],
    // APPROVED, CANCELLED, and MODIFIED are terminal states (no outgoing transitions)
    [RequestStatus.APPROVED, []],
    [RequestStatus.CANCELLED, []],
    [RequestStatus.MODIFIED, []],
]);

/**
 * Checks if a transition from one status to another is allowed
 */
export function canTransition(from: RequestStatus, to: RequestStatus): boolean {
    const allowedTransitions = ALLOWED_TRANSITIONS.get(from) ?? [];
    return allowedTransitions.includes(to);
}

/**
 * Gets all allowed transitions from a given status
 */
export function getAllowedTransitions(from: RequestStatus): RequestStatus[] {
    return ALLOWED_TRANSITIONS.get(from) ?? [];
}

/**
 * Checks if a status is a terminal state (no outgoing transitions)
 */
export function isTerminalState(status: RequestStatus): boolean {
    const allowedTransitions = ALLOWED_TRANSITIONS.get(status) ?? [];
    return allowedTransitions.length === 0;
}

/**
 * Creates a history entry for a status transition
 */
export function createHistoryEntry(
    fromStatus: RequestStatus,
    toStatus: RequestStatus,
    changedBy: string,
    notes?: string,
): RequestHistoryEntry {
    return {
        timestamp: new Date(),
        fromStatus,
        toStatus,
        changedBy,
        notes,
    };
}

/**
 * Adds a history entry to a request and updates its status
 */
export function addHistoryEntry(
    request: Request,
    fromStatus: RequestStatus,
    toStatus: RequestStatus,
    changedBy: string,
    notes?: string,
): Request {
    // Verify the transition is allowed
    if (!canTransition(fromStatus, toStatus)) {
        throw new Error(
            `Invalid transition: ${fromStatus} -> ${toStatus} is not allowed.`,
        );
    }

    const newEntry: RequestHistoryEntry = createHistoryEntry(
        fromStatus,
        toStatus,
        changedBy,
        notes,
    );

    return {
        ...request,
        status: toStatus,
        history: [...request.history, newEntry],
        updatedAt: new Date(),
    };
}

/**
 * Transitions a request from DRAFT to SUBMITTED
 */
export function transitionToDelivered(
    request: Request,
    changedBy: string,
): Request {
    return addHistoryEntry(
        request,
        RequestStatus.DRAFT,
        RequestStatus.SUBMITTED,
        changedBy,
        'Søknad innsendt',
    );
}

/**
 * Transitions a request from REJECTED back to SUBMITTED (resubmission)
 */
export function resubmitFromDeclined(
    request: Request,
    changedBy: string,
): Request {
    return addHistoryEntry(
        request,
        RequestStatus.REJECTED,
        RequestStatus.SUBMITTED,
        changedBy,
        'Søknad sendt på nytt',
    );
}

/**
 * Transitions a request from SUBMITTED to CANCELLED
 */
export function transitionToCancelled(
    request: Request,
    changedBy: string,
): Request {
    return addHistoryEntry(
        request,
        RequestStatus.SUBMITTED,
        RequestStatus.CANCELLED,
        changedBy,
        'Søknad kansellert',
    );
}
